import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, ShoppingCart, Star } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { baseURL } from '../../utils/api';
import { useCachedFetch } from '../../utils/cache';
import './Shop.css';

const Shop = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const { addToCart } = useCart();

    const { data: productsData, loading } = useCachedFetch('/api/products', { ttl: 10 * 60 * 1000 });
    const products = useMemo(() => (Array.isArray(productsData) ? productsData : []), [productsData]);

    const filteredProducts = useMemo(() => {
        let result = products;
        if (selectedCategory !== 'All') {
            result = result.filter(p => p.category === selectedCategory);
        }
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(p =>
                p.name.toLowerCase().includes(q) ||
                (p.description || '').toLowerCase().includes(q)
            );
        }
        return result;
    }, [products, searchQuery, selectedCategory]);

    const categories = useMemo(() => ['All', ...new Set(products.map(p => p.category))], [products]);

    return (
        <div className="ec-shop-root">
            <header className="ec-shop-header">
                <h1>Our Collection</h1>
                <p>Meticulously crafted essentials for your every day.</p>
            </header>

            <div className="ec-shop-container">
                {/* Sidebar / Filters */}
                <aside className="ec-shop-sidebar">
                    <div className="ec-search-box">
                        <Search size={18} />
                        <input 
                            type="text" 
                            placeholder="Search products..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="ec-filter-section">
                        <h4>Categories</h4>
                        <div className="ec-cat-list">
                            {categories.map(cat => (
                                <button 
                                    key={cat}
                                    className={`ec-cat-btn ${selectedCategory === cat ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(cat)}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* Products Grid */}
                <main className="ec-shop-main">
                    <div className="ec-results-bar">
                        <p>{filteredProducts.length} Products Found</p>
                        <div className="ec-sort-select">
                            <Filter size={14} />
                            <span>Sort by: Latest</span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="ec-loading-state">
                            <div className="ec-spinner"></div>
                            <p>Loading curated collection...</p>
                        </div>
                    ) : (
                        <div className="ec-shop-grid">
                            {filteredProducts.map(product => (
                                <div key={product.id} className="ec-pcard">
                                    <Link to={`/product/${product.slug}`} className="ec-pcard-img">
                                        <img src={product.image ? (product.image.startsWith('http') ? product.image : `${baseURL}${product.image}`) : 'https://via.placeholder.com/300'} alt={product.name} />
                                    </Link>
                                    <div className="ec-pcard-body">
                                        <div className="ec-pcard-header">
                                            <span className="ec-pcard-cat">{product.category}</span>
                                            <div className="ec-pcard-rating">
                                                <Star size={12} fill="#fbaf00" color="#fbaf00" />
                                                <span>4.8</span>
                                            </div>
                                        </div>
                                        <Link to={`/product/${product.slug}`} className="ec-pcard-title">
                                            {product.name}
                                        </Link>
                                        <div className="ec-pcard-footer">
                                            <span className="ec-pcard-price">Rs. {product.price.toLocaleString()}</span>
                                            <button 
                                                className="ec-pcard-add"
                                                onClick={() => addToCart(product)}
                                            >
                                                <ShoppingCart size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {filteredProducts.length === 0 && (
                                <div className="ec-no-results">
                                    <h3>No products found</h3>
                                    <p>Try adjusting your search or category filter.</p>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Shop;
